import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormData, nextStep } from '../../store/formSlice';
import {
    Button,
    Box,
    Text,
    VStack,
    Radio,
    RadioGroup,
    FormControl,
    FormErrorMessage,
    HStack,
    FormLabel,
} from '@chakra-ui/react';
import { TimePicker, DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import StepHeader from './StepHeader';

const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Everyday this week' },
    { value: 'twice', label: '2 times a week' },
    { value: 'weekly', label: 'Once a week' },
];

// Utility functions
// Converts epoch seconds to { date: 'YYYY-MM-DD', time: 'HH:mm:ss' }
const parseDateTimeFromUnix = (unixTimestamp) => {
    const date = new Date(unixTimestamp * 1000);
    return {
        date: date.toISOString().split('T')[0],
        time: date.toTimeString().slice(0, 8) // HH:mm:ss
    };
};

// Converts scheduleList (array of {scheduleTime}) to { date1, time1, date2, time2 } in string format
const convertScheduleListToDateTime = (scheduleList) => {
    if (!scheduleList || scheduleList.length === 0) {
        return { date1: '', time1: '', date2: '', time2: '' };
    }
    const result = { date1: '', time1: '', date2: '', time2: '' };
    if (scheduleList.length > 0) {
        const firstSchedule = parseDateTimeFromUnix(scheduleList[0].scheduleTime);
        result.date1 = firstSchedule.date;
        result.time1 = firstSchedule.time;
    }
    if (scheduleList.length > 1) {
        const secondSchedule = parseDateTimeFromUnix(scheduleList[1].scheduleTime);
        result.date2 = secondSchedule.date;
        result.time2 = secondSchedule.time;
    }
    return result;
};

// Converts date (YYYY-MM-DD) and time (HH:mm or HH:mm:ss) to epoch seconds
const createEpochFromDateTime = (date, time) => {
    if (!date || !time) return null;
    // Ensure time is in HH:mm:ss
    const timeWithSeconds = time.length === 5 ? `${time}:00` : time;
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes, seconds] = timeWithSeconds.split(':').map(Number);
    const dt = new Date(year, month - 1, day, hours, minutes, seconds, 0);
    return Math.floor(dt.getTime() / 1000);
};

// Checks if the given date and time (as strings) are in the future
const isDateTimeInFuture = (date, time) => {
    if (!date || !time) return false;
    const epoch = createEpochFromDateTime(date, time);
    return epoch && epoch > Math.floor(Date.now() / 1000);
};

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getEndOfWeek = () => {
    const today = new Date();
    const endOfWeek = new Date(today);
    const daysUntilSunday = 7 - today.getDay();
    endOfWeek.setDate(today.getDate() + daysUntilSunday);
    return endOfWeek;
};

// Wrap the component in React.memo for optimization
const CallFrequencyStep = React.memo(() => {
    const dispatch = useDispatch();
    const { formData } = useSelector((state) => state.form);

    // Initialize form state from Redux data
    const initializeFormState = useCallback(() => {
        const { callFrequency, scheduleList } = formData;

        if (!callFrequency || !scheduleList || scheduleList.length === 0) {
            return {
                callFrequency: callFrequency || '',
                date1: '',
                time1: '',
                date2: '',
                time2: '',
                step: 1,
                errors: { frequency: '', datetime: '' }
            };
        }

        // Extract date/time from scheduleList
        const { date1, time1, date2, time2 } = convertScheduleListToDateTime(scheduleList);

        return {
            callFrequency,
            date1,
            time1,
            date2,
            time2,
            step: 2, // If we have data, go to step 2
            errors: { frequency: '', datetime: '' }
        };
    }, [formData]);

    const [formState, setFormState] = useState(initializeFormState);

    // Memoized validation
    const validation = useMemo(() => {
        const { callFrequency, date1, time1, date2, time2 } = formState;

        const isStep1Valid = !!callFrequency;

        let isStep2Valid = false;
        if (callFrequency === 'twice') {
            isStep2Valid = date1 && time1 && date2 && time2 &&
                isDateTimeInFuture(date1, time1) &&
                isDateTimeInFuture(date2, time2);
        } else if (callFrequency === 'daily') {
            isStep2Valid = time1;
        } else {
            isStep2Valid = date1 && time1 && isDateTimeInFuture(date1, time1);
        }

        return { isStep1Valid, isStep2Valid };
    }, [formState]);

    // Optimized state updates
    const updateFormState = useCallback((updates) => {
        setFormState(prev => ({
            ...prev,
            ...updates,
            errors: { ...prev.errors, ...(updates.errors || {}) }
        }));
    }, []);

    // Event handlers
    const handleFrequencyChange = useCallback((value) => {
        updateFormState({
            callFrequency: value,
            errors: { frequency: '' }
        });
    }, [updateFormState]);

    const createDateTimeHandler = useCallback((field) => (value) => {
        const stringValue = value ? value.format(field.includes('time') ? 'HH:mm' : 'YYYY-MM-DD') : '';
        updateFormState({
            [field]: stringValue,
            errors: { datetime: '' }
        });
    }, [updateFormState]);

    const handleDate1Change = createDateTimeHandler('date1');
    const handleTime1Change = createDateTimeHandler('time1');
    const handleDate2Change = createDateTimeHandler('date2');
    const handleTime2Change = createDateTimeHandler('time2');

    // Validation functions
    const validateStep1 = useCallback(() => {
        if (!formState.callFrequency) {
            updateFormState({ errors: { frequency: 'Please select a call frequency' } });
            return false;
        }
        updateFormState({ errors: { frequency: '' } });
        return true;
    }, [formState.callFrequency, updateFormState]);

    const validateStep2 = useCallback(() => {
        const { callFrequency, date1, time1, date2, time2 } = formState;
        let errorMessage = '';

        if (callFrequency === 'twice') {
            if (!date1 || !time1 || !date2 || !time2) {
                errorMessage = 'Please select both dates and times';
            } else if (!isDateTimeInFuture(date1, time1) || !isDateTimeInFuture(date2, time2)) {
                errorMessage = 'Please select future dates and times';
            }
        } else if (callFrequency === 'daily') {
            if (!time1) {
                errorMessage = 'Please select time';
            }
        } else {
            if (!date1 || !time1) {
                errorMessage = 'Please select date and time';
            } else if (!isDateTimeInFuture(date1, time1)) {
                errorMessage = 'Please select a future date and time';
            }
        }

        if (errorMessage) {
            updateFormState({ errors: { datetime: errorMessage } });
            return false;
        }

        updateFormState({ errors: { datetime: '' } });
        return true;
    }, [formState, updateFormState]);

    // Optimized schedule generation
    const generateScheduleList = useCallback(() => {
        const { callFrequency, date1, time1, date2, time2 } = formState;
        const scheduleList = [];

        const makeSchedule = (date, time) => {
            const scheduleTime = createEpochFromDateTime(date, time);
            // For scheduleDate, use start of the day (00:00:00)
            const scheduleDate = createEpochFromDateTime(date, '00:00:00');
            return {
                scheduleDate,
                scheduleTime
            };
        };

        switch (callFrequency) {
            case 'daily': {
                const today = new Date();
                const endOfWeek = getEndOfWeek();
                for (let i = 0; i <= 6; i++) {
                    const nextDay = new Date(today);
                    nextDay.setDate(today.getDate() + i);
                    if (nextDay <= endOfWeek) {
                        const dateStr = formatDate(nextDay);
                        // Always use HH:mm:ss
                        const timeStr = time1.length === 5 ? `${time1}:00` : time1;
                        const fullTimeEpoch = createEpochFromDateTime(dateStr, timeStr);
                        if (fullTimeEpoch > Math.floor(Date.now() / 1000)) {
                            scheduleList.push(makeSchedule(dateStr, timeStr));
                        }
                    }
                }
                break;
            }
            case 'twice':
                scheduleList.push(makeSchedule(date1, time1.length === 5 ? `${time1}:00` : time1));
                scheduleList.push(makeSchedule(date2, time2.length === 5 ? `${time2}:00` : time2));
                break;
            case 'weekly':
                scheduleList.push(makeSchedule(date1, time1.length === 5 ? `${time1}:00` : time1));
                break;
        }
        return scheduleList;
    }, [formState]);

    // Submit handler
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const { step, callFrequency } = formState;

        if (step === 1) {
            if (validateStep1()) {
                updateFormState({ step: 2 });
            }
        } else if (step === 2) {
            if (validateStep2()) {
                const scheduleList = generateScheduleList();

                dispatch(updateFormData({
                    callFrequency,
                    scheduleList
                }));

                // Convert scheduleList back to date/time format for debugging
                const convertedDateTime = convertScheduleListToDateTime(scheduleList);
                dispatch(nextStep());
            }
        }
    }, [formState, validateStep1, validateStep2, generateScheduleList, dispatch]);

    const handlePrevious = useCallback(() => {
        updateFormState({ step: 1 });
    }, [updateFormState]);

    // Memoized render data
    const renderData = useMemo(() => ({
        callFrequency: formState.callFrequency,
        date1: formState.date1,
        time1: formState.time1,
        date2: formState.date2,
        time2: formState.time2,
        step: formState.step,
        errors: formState.errors
    }), [formState]);

    return (
        <Box maxW="3xl" mx="auto" p={6} textAlign="center">
            <StepHeader
                title="Call Frequency"
                subtitle={`How often would you like Sophie to call ${formData.firstName || "First Name"}?`}
            />
            <form onSubmit={handleSubmit}>
                {renderData.step === 1 && (
                    <FormControl isInvalid={!!renderData.errors.frequency} mb={8}>
                        <RadioGroup
                            onChange={handleFrequencyChange}
                            value={renderData.callFrequency}
                        >
                            <VStack spacing={4} align="center">
                                {FREQUENCY_OPTIONS.map((option) => {
                                    const selected = renderData.callFrequency === option.value;
                                    return (
                                        <Box
                                            key={option.value}
                                            bg="white"
                                            border="1px solid"
                                            borderRadius="lg"
                                            borderColor={selected ? 'brand.500' : 'gray.200'}
                                            p={{ base: 3, md: 4 }}
                                            w="100%"
                                            maxW="100%"
                                            boxShadow={selected ? 'md' : 'sm'}
                                            transition="all 0.2s"
                                        >
                                            <HStack>
                                                <Radio
                                                    value={option.value}
                                                    colorScheme="brand"
                                                    size="lg"
                                                    borderColor={selected ? 'brand.500' : 'gray.300'}
                                                    isChecked={selected}
                                                >
                                                    <Text
                                                        fontSize={{ base: '14px', md: '20px' }}
                                                        color="black"
                                                        fontWeight="normal"
                                                        fontFamily="Nunito"
                                                        pl={2}
                                                        m={0}
                                                    >
                                                        {option.label}
                                                    </Text>
                                                </Radio>
                                            </HStack>
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </RadioGroup>
                        <FormErrorMessage>{renderData.errors.frequency}</FormErrorMessage>
                    </FormControl>
                )}

                {renderData.step === 2 && (
                    <FormControl isInvalid={!!renderData.errors.datetime} mb={8}>
                        <FormLabel textAlign="center">
                            {renderData.callFrequency === 'daily'
                                ? 'Select Time (will be used for everyday this week)'
                                : 'Select Date & Time'
                            }
                        </FormLabel>
                        <VStack spacing={4} align="center" w="100%">
                            <ConfigProvider
                                theme={{
                                    token: {
                                        colorPrimary: '#108084', // hover color (optional)
                                    },
                                }}
                            >
                                {renderData.callFrequency === 'daily' ? (
                                    <HStack w="100%" justifyContent="center">
                                        <TimePicker
                                            hoverBorderColor="#108084"
                                            value={renderData.time1 ? dayjs(renderData.time1, 'HH:mm') : null}
                                            onChange={handleTime1Change}
                                            format="HH:mm"
                                            style={{ width: '160px' }}
                                            placeholder="Select time"
                                            size="large"
                                        />
                                    </HStack>
                                ) : (
                                    <HStack w="100%" justifyContent="center">
                                        <DatePicker
                                            hoverBorderColor="#108084"
                                            value={renderData.date1 ? dayjs(renderData.date1) : null}
                                            onChange={handleDate1Change}
                                            format="YYYY-MM-DD"
                                            style={{ width: '160px' }}
                                            placeholder="Select date"
                                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                                            size="large"
                                        />
                                        <TimePicker
                                            hoverBorderColor="#108084"
                                            value={renderData.time1 ? dayjs(renderData.time1, 'HH:mm') : null}
                                            onChange={handleTime1Change}
                                            format="HH:mm"
                                            style={{ width: '160px' }}
                                            placeholder="Select time"
                                            size="large"
                                        />
                                    </HStack>
                                )}
                                {renderData.callFrequency === 'twice' && (
                                    <HStack w="100%" justifyContent="center">
                                        <DatePicker
                                            hoverBorderColor="#108084"
                                            value={renderData.date2 ? dayjs(renderData.date2) : null}
                                            onChange={handleDate2Change}
                                            format="YYYY-MM-DD"
                                            style={{ width: '160px' }}
                                            placeholder="Select date"
                                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                                            size="large"
                                        />
                                        <TimePicker
                                            hoverBorderColor="#108084"
                                            value={renderData.time2 ? dayjs(renderData.time2, 'HH:mm') : null}
                                            onChange={handleTime2Change}
                                            format="HH:mm"
                                            style={{ width: '160px' }}
                                            placeholder="Select time"
                                            size="large"
                                        />
                                    </HStack>
                                )}
                            </ConfigProvider>
                        </VStack>
                        <FormErrorMessage>{renderData.errors.datetime}</FormErrorMessage>
                    </FormControl>
                )}

                <Text fontSize="16px" color="black" mb={4} mt={2}>
                    You can adjust the schedule anytime.
                </Text>

                <HStack justify="center">
                    {renderData.step === 2 && (
                        <Button
                            mt={4}
                            px="30px"
                            variant="outline"
                            onClick={handlePrevious}
                            type="button"
                        >
                            Previous
                        </Button>
                    )}
                    <Button
                        mt={4}
                        px="30px"
                        isDisabled={renderData.step === 1 ? !validation.isStep1Valid : !validation.isStep2Valid}
                        type='submit'
                    >
                        Continue
                    </Button>
                </HStack>
            </form>
        </Box>
    );
});

export default CallFrequencyStep;
